'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Flights', href: '/', icon: 'PaperAirplaneIcon' },
  { label: 'Flight Search', href: '/flight-search', icon: 'MagnifyingGlassIcon' },
  { label: 'Hotels', href: '/', icon: 'BuildingOffice2Icon' },
  { label: 'Packages', href: '/', icon: 'GiftIcon' },
  { label: 'Deals', href: '/', icon: 'TagIcon' },
];

interface TopbarProps {
  transparent?: boolean;
}

export default function Topbar({ transparent = false }: TopbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  const isAuthPage = pathname === '/sign-up-login-screen';

  const baseClass = transparent
    ? 'fixed top-0 left-0 right-0 z-50 transition-all duration-300' :'sticky top-0 z-50 bg-card border-b border-border shadow-sm';

  return (
    <>
      <header className={baseClass}>
        <div
          className={
            transparent
              ? 'topbar-blur bg-white/10 border-b border-white/20' :''
          }
        >
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
                <AppLogo size={36} />
                <span
                  className={`font-extrabold text-xl tracking-tight hidden sm:block ${
                    transparent ? 'text-white' : 'text-primary'
                  }`}
                >
                  TravelHub
                </span>
              </Link>

              {/* Desktop Nav */}
              {!isAuthPage && (
                <nav className="hidden md:flex items-center gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={`nav-${link.label}`}
                      href={link.href}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-500 transition-colors duration-150 ${
                        transparent
                          ? 'text-white/80 hover:text-white hover:bg-white/10' :'text-muted-foreground hover:text-foreground hover:bg-secondary'
                      }`}
                    >
                      <Icon name={link.icon as any} size={16} />
                      {link.label}
                    </Link>
                  ))}
                </nav>
              )}

              {/* Right Actions */}
              {!isAuthPage && (
                <div className="hidden md:flex items-center gap-2">
                  <Link
                    href="/sign-up-login-screen"
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                      transparent
                        ? 'text-white hover:bg-white/10' :'text-primary hover:bg-secondary'
                    }`}
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/sign-up-login-screen"
                    className="btn-primary px-4 py-2 text-sm"
                  >
                    Sign Up Free
                  </Link>
                </div>
              )}

              {/* Mobile Hamburger */}
              {!isAuthPage && (
                <button
                  onClick={() => setMobileOpen(true)}
                  className={`md:hidden p-2 rounded-lg transition-colors ${
                    transparent ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-muted'
                  }`}
                  aria-label="Open navigation menu"
                >
                  <Icon name="Bars3Icon" size={22} />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div
            className="mobile-menu-overlay absolute inset-0 bg-foreground/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="mobile-menu-drawer absolute left-0 top-0 bottom-0 w-72 bg-card shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <AppLogo size={32} />
                <span className="font-extrabold text-lg text-primary">TravelHub</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Close navigation menu"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>

            <nav className="flex-1 p-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={`mobile-nav-${link.label}`}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-secondary transition-colors"
                >
                  <Icon name={link.icon as any} size={18} className="text-primary" />
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="p-4 border-t border-border space-y-2">
              <Link
                href="/sign-up-login-screen"
                onClick={() => setMobileOpen(false)}
                className="block w-full text-center btn-secondary px-4 py-2.5 text-sm"
              >
                Sign In
              </Link>
              <Link
                href="/sign-up-login-screen"
                onClick={() => setMobileOpen(false)}
                className="block w-full text-center btn-primary px-4 py-2.5 text-sm"
              >
                Sign Up Free
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}